import React, { useState, useEffect } from 'react'
import { CiSettings } from "react-icons/ci"
import { BsFillQuestionCircleFill } from "react-icons/bs"
import { MdMailOutline, MdPhoneAndroid } from "react-icons/md"
import { FaWhatsapp, FaEdit, FaTrash, FaKey, FaLink, FaFilter, FaUndo } from "react-icons/fa"
import axios from 'axios'

// ─── Token helpers ────────────────────────────────────────────────────────────
function decodeToken(token) {
  if (!token) return {}
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
    )
    return JSON.parse(jsonPayload)
  } catch {
    return {}
  }
}

function getAuthFromStorage() {
  if (typeof window === 'undefined') return { userId: null, token: null }
  const raw =
    typeof window.locatorgarsotrga === 'function'
      ? window.locatorgarsotrga('token')
      : localStorage.getItem('token')
  if (!raw) return { userId: null, token: null }
  const decoded = decodeToken(raw)
  return { userId: decoded.id || decoded.userId || decoded._id || null, token: raw }
}

// ─── Toggle Switch ────────────────────────────────────────────────────────────
function Toggle({ value, onChange, label, tooltip }) {
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <button
        type="button"
        onClick={() => onChange(!value)}
        className={`w-[36px] h-[18px] flex items-center rounded-full transition-all duration-300 flex-shrink-0 ${
          value ? "bg-[#37715d]" : "bg-[#b4bbc3]"
        }`}
        aria-pressed={value}
      >
        <div
          className={`bg-white w-[14px] h-[14px] rounded-full shadow transform transition-all duration-300 ml-[2px] ${
            value ? "translate-x-[17px]" : "translate-x-0"
          }`}
        />
      </button>
      <span
        onClick={() => onChange(!value)}
        className="text-[#576071] text-[13px] cursor-pointer select-none"
      >
        {label}
      </span>
      <div className="relative group">
        <BsFillQuestionCircleFill className="text-[#3f7fca] cursor-pointer" />
        <span className="hidden group-hover:block absolute left-1/2 -translate-x-1/2 -top-8 bg-white border border-gray-200 text-xs text-gray-600 rounded px-2 py-1 whitespace-nowrap shadow z-10">
          {tooltip}
        </span>
      </div>
    </div>
  )
}

// ─── Account Settings Tab ─────────────────────────────────────────────────────
function AccountSettingsTab({ user_id }) {
  const [settings, setSettings] = useState({
    IsWAApiEnabled: false,
    IsSecureReporting: false,
    InternationalAccount: false,
  })
  const [notification, setNotification] = useState(null)

  useEffect(() => {
    if (!user_id) return
    fetch('/Admin/WhatsAppConfiguration/GetUserSettings', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: `user_id=${encodeURIComponent(user_id)}`,
    })
      .then(r => r.json())
      .then(res => {
        if (res.success) {
          setSettings({
            IsWAApiEnabled: res.data.isWAApiEnabled,
            IsSecureReporting: res.data.isSecureReporting,
            InternationalAccount: res.data.internationalAccount,
          })
        }
      })
      .catch(() => {})
  }, [user_id])

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }))
    fetch('/Admin/WhatsAppConfiguration/UpdateSettingField', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: `user_id=${encodeURIComponent(user_id)}&settingName=${key}&settingValue=${value}`,
    })
      .then(r => r.json())
      .then(res => {
        setNotification({ type: res.success ? 'success' : 'danger', msg: res.message })
        setTimeout(() => setNotification(null), 3000)
      })
      .catch(() => {})
  }

  return (
    <div className="py-4">
      {notification && (
        <div
          className={`mb-4 px-4 py-2 rounded text-sm font-medium ${
            notification.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          {notification.msg}
        </div>
      )}
      <div className="flex flex-col md:flex-row gap-6 flex-wrap">
        <Toggle
          value={settings.IsWAApiEnabled}
          onChange={v => updateSetting('IsWAApiEnabled', v)}
          label="WhatsApp API Enabled"
          tooltip="Enable or disable WhatsApp API"
        />
        <Toggle
          value={settings.IsSecureReporting}
          onChange={v => updateSetting('IsSecureReporting', v)}
          label="Secure Reporting"
          tooltip="Enable or disable secure reporting"
        />
        <Toggle
          value={settings.InternationalAccount}
          onChange={v => updateSetting('InternationalAccount', v)}
          label="International Account"
          tooltip="Enable or disable international account"
        />
      </div>
    </div>
  )
}

// ─── Message Management Tab ───────────────────────────────────────────────────
function MessageManagementTab({ user_id }) {
  const handleViewSentMessages = () => {
    window.open(
      `/Admin/WhatsAppConfiguration/SentMessageDeliveryIndex?user_id=${encodeURIComponent(user_id)}`,
      '',
      'width=1250,height=700,scrollbars=yes'
    )
  }

  return (
    <div className="py-4">
      <button
        onClick={handleViewSentMessages}
        className="flex items-center gap-2 px-4 py-2 border border-[#17a2b8] text-[#17a2b8] rounded hover:bg-[#17a2b8] hover:text-white transition-colors text-sm font-medium"
      >
        <FaWhatsapp className="text-base" />
        Sent Messages
      </button>
    </div>
  )
}

// ─── Channel List Tab ─────────────────────────────────────────────────────────
function normaliseChannel(row) {
  return {
    accountId:      row.account_id,
    wabaId:         row.waba_id,
    phoneNumberId:  row.phone_number_id,
    wabaNumber:     row['WABA Number']      ?? row.display_phone_number ?? '',
    wabaBusinessId: row['WABA BusinessId']  ?? row.waba_id ?? '',
    qualityRating:  row['Quality Rating']   ?? '',
    displayName:    row['Display Name']     ?? row.verified_name ?? '',
    configureDate:  row['ConfigureDate']    ?? row.created_at ?? '',
  }
}

function ChannelListTab({ user_id }) {
  const [channels, setChannels]       = useState([])
  const [loading, setLoading]         = useState(false)
  const [error, setError]             = useState(null)
  const [filterWABA, setFilterWABA]   = useState('')
  const [filterPhone, setFilterPhone] = useState('')
  const [filterOpen, setFilterOpen]   = useState(false)

  const [webhookModal, setWebhookModal] = useState({ open: false, accountId: null, url: '' })
  const [tpsModal, setTpsModal]         = useState({ open: false, accountId: null, phoneNumberId: null, tps: '', webhookType: '0' })
  const [notification, setNotification] = useState(null)

  const showNotif = (type, msg) => {
    setNotification({ type, msg })
    setTimeout(() => setNotification(null), 3500)
  }

  // ── Fetch channels ──────────────────────────────────────────────────────────
  const fetchChannels = async () => {
    if (!user_id) return
    setLoading(true)
    setError(null)
    try {
      const { token } = getAuthFromStorage()
      const apiBase = process.env.REACT_APP_API_URL || 'https://wa-api.globebot.io'
      const headers = token ? { Authorization: token } : {}
      const resp = await axios.get(
        `${apiBase}/auth/whatsappauthid?id=${encodeURIComponent(user_id)}`,
        { headers }
      )
      const rows = Array.isArray(resp.data)
        ? resp.data
        : Array.isArray(resp.data?.data)
          ? resp.data.data
          : []
      setChannels(rows.map(normaliseChannel))
    } catch (err) {
      setError('Failed to load channels. Please try again.')
      setChannels([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchChannels() }, [user_id])

  // ── Client-side filter ──────────────────────────────────────────────────────
  const displayData = channels.filter(ch => {
    const wabaOk  = !filterWABA  || ch.wabaNumber.includes(filterWABA.trim())
    const phoneOk = !filterPhone || ch.phoneNumberId?.toString().includes(filterPhone.trim())
    return wabaOk && phoneOk
  })

  const handleClear = () => { setFilterWABA(''); setFilterPhone('') }

  // ── Register API ────────────────────────────────────────────────────────────
  const registerAPI = async (phoneNumberId) => {
    if (!window.confirm('Do you really want to call Register API against this number?')) return
    try {
      const { token } = getAuthFromStorage()
      const apiBase = process.env.REACT_APP_API_URL || 'https://wa-api.globebot.io'
      const headers = token ? { Authorization: token } : {}
      const res = await axios.post(
        `${apiBase}/auth/register-phone`,
        { phoneNumberId },
        { headers }
      )
      showNotif('success', res.data?.message || 'Registered successfully.')
      fetchChannels()
    } catch (err) {
      showNotif('danger', err.response?.data?.message || 'Registration failed.')
    }
  }

  // ── Delete channel ──────────────────────────────────────────────────────────
  const deleteChannel = async (accountId) => {
    if (!window.confirm('Do you really want to delete this channel?')) return
    try {
      const { token } = getAuthFromStorage()
      const apiBase = process.env.REACT_APP_API_URL || 'https://wa-api.globebot.io'
      const headers = token ? { Authorization: token } : {}
      const res = await axios.delete(`${apiBase}/auth/whatsappauthid/${accountId}`, { headers })
      showNotif('success', res.data?.message || 'Deleted successfully.')
      fetchChannels()
    } catch (err) {
      showNotif('danger', err.response?.data?.message || 'Delete failed.')
    }
  }

  // ── Webhook modal ───────────────────────────────────────────────────────────
  const openWebhookModal = (accountId, url) => setWebhookModal({ open: true, accountId, url: url || '' })

  const saveWebhook = async () => {
    try {
      const { token } = getAuthFromStorage()
      const apiBase = process.env.REACT_APP_API_URL || 'https://wa-api.globebot.io'
      const headers = token ? { Authorization: token } : {}
      const res = await axios.patch(
        `${apiBase}/auth/whatsappauthid/${webhookModal.accountId}`,
        { webhookUrl: webhookModal.url },
        { headers }
      )
      showNotif('success', res.data?.message || 'Webhook updated.')
      setWebhookModal({ open: false, accountId: null, url: '' })
      fetchChannels()
    } catch (err) {
      showNotif('danger', err.response?.data?.message || 'Failed to update webhook.')
    }
  }

  // ── TPS modal ───────────────────────────────────────────────────────────────
  const openTpsModal = (accountId, phoneNumberId) =>
    setTpsModal({ open: true, accountId, phoneNumberId, tps: '', webhookType: '0' })

  const saveTPS = async () => {
    if (!tpsModal.tps || Number(tpsModal.tps) < 0) {
      showNotif('danger', 'Please enter a valid TPS value.')
      return
    }
    try {
      const { token } = getAuthFromStorage()
      const apiBase = process.env.REACT_APP_API_URL || 'https://wa-api.globebot.io'
      const headers = token ? { Authorization: token } : {}
      const res = await axios.patch(
        `${apiBase}/auth/whatsappauthid/${tpsModal.accountId}`,
        { tps: Number(tpsModal.tps), userWebhookType: Number(tpsModal.webhookType) },
        { headers }
      )
      showNotif('success', res.data?.message || 'Updated successfully.')
      setTpsModal({ open: false, accountId: null, phoneNumberId: null, tps: '', webhookType: '0' })
      fetchChannels()
    } catch (err) {
      showNotif('danger', err.response?.data?.message || 'Failed to update.')
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="py-2">
      {notification && (
        <div className={`mb-3 px-4 py-2 rounded text-sm font-medium ${
          notification.type === 'success'
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {notification.msg}
        </div>
      )}

      {/* Filter Accordion */}
      <div className="border border-gray-200 rounded mb-4">
        <button
          type="button"
          className="w-full flex items-center gap-2 px-4 py-2 text-sm font-medium text-left bg-gray-50 rounded hover:bg-gray-100 transition"
          onClick={() => setFilterOpen(o => !o)}
        >
          <FaFilter className="text-gray-400 text-xs" />
          Filter Records
          <span className="ml-auto text-gray-400">{filterOpen ? '▲' : '▼'}</span>
        </button>
        {filterOpen && (
          <div className="px-4 py-3 flex flex-wrap gap-3 items-end">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">WABA Number</label>
              <input
                type="text"
                value={filterWABA}
                onChange={e => setFilterWABA(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-sm w-44"
                placeholder="WABA Number"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">Phone Number Id</label>
              <input
                type="text"
                value={filterPhone}
                onChange={e => setFilterPhone(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-sm w-44"
                placeholder="Phone Number Id"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setFilterOpen(false)}
                className="flex items-center gap-1 px-3 py-1.5 border border-blue-500 text-blue-600 text-sm rounded hover:bg-blue-50 transition"
              >
                <FaFilter className="text-xs" /> Apply
              </button>
              <button
                onClick={handleClear}
                className="flex items-center gap-1 px-3 py-1.5 border border-teal-500 text-teal-600 text-sm rounded hover:bg-teal-50 transition"
              >
                <FaUndo className="text-xs" /> Clear
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="w-8 h-8 border-4 border-teal-400 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="text-center text-red-400 text-sm py-8">{error}</div>
      ) : displayData.length === 0 ? (
        <div className="text-center text-gray-400 text-sm py-8">No channel data available.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                {[
                  'WABA Number', 'Phone Number Id', 'WABA BusinessId',
                  'Quality Rating', 'Display Name', 'Configure Date', 'Action'
                ].map(h => (
                  <th key={h} className="px-3 py-2 text-left border-b border-gray-200 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayData.map((ch, i) => (
                <tr key={ch.accountId ?? i} className="border-b border-gray-100 hover:bg-gray-50 transition">
                  <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">{ch.wabaNumber}</td>
                  <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">{ch.phoneNumberId}</td>
                  <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">{ch.wabaBusinessId}</td>
                  <td className="px-3 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      ch.qualityRating === 'GREEN'  ? 'bg-green-100 text-green-700' :
                      ch.qualityRating === 'YELLOW' ? 'bg-yellow-100 text-yellow-700' :
                      ch.qualityRating === 'RED'    ? 'bg-red-100 text-red-600' :
                      'bg-gray-100 text-gray-500'
                    }`}>
                      {ch.qualityRating || '—'}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{ch.displayName}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                    {ch.configureDate ? new Date(ch.configureDate).toLocaleString() : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openTpsModal(ch.accountId, ch.phoneNumberId)}
                        title="Edit TPS / Webhook Type"
                        className="p-1.5 border border-teal-400 text-teal-600 rounded hover:bg-teal-50 transition"
                      >
                        <FaEdit className="text-xs" />
                      </button>
                      <button
                        onClick={() => openWebhookModal(ch.accountId, '')}
                        title="Update Webhook URL"
                        className="p-1.5 border border-blue-300 text-blue-500 rounded hover:bg-blue-50 transition"
                      >
                        <FaLink className="text-xs" />
                      </button>
                      <button
                        onClick={() => deleteChannel(ch.accountId)}
                        title="Delete"
                        className="p-1.5 border border-red-400 text-red-500 rounded hover:bg-red-50 transition"
                      >
                        <FaTrash className="text-xs" />
                      </button>
                      <button
                        onClick={() => registerAPI(ch.phoneNumberId)}
                        title="Register API"
                        className="p-1.5 border border-purple-400 text-purple-500 rounded hover:bg-purple-50 transition"
                      >
                        <FaKey className="text-xs" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-teal-600 mt-2">
            Showing {displayData.length} of {channels.length} records
          </p>
        </div>
      )}

      {/* Webhook Modal */}
      {webhookModal.open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <h5 className="font-semibold text-gray-700">Update Webhook URL</h5>
              <button
                onClick={() => setWebhookModal({ open: false, accountId: null, url: '' })}
                className="text-gray-400 hover:text-gray-600 text-xl"
              >
                &times;
              </button>
            </div>
            <div className="px-5 py-4">
              <label className="text-sm text-gray-600 mb-1 block">Webhook URL</label>
              <input
                type="text"
                value={webhookModal.url}
                onChange={e => setWebhookModal(m => ({ ...m, url: e.target.value }))}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
                placeholder="https://your-webhook-endpoint.com/..."
              />
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t">
              <button
                onClick={() => setWebhookModal({ open: false, accountId: null, url: '' })}
                className="px-4 py-1.5 border border-gray-300 text-gray-600 text-sm rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={saveWebhook}
                className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TPS / Webhook Type Modal */}
      {tpsModal.open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <h5 className="font-semibold text-gray-700">Update Details</h5>
              <button
                onClick={() => setTpsModal({ open: false, accountId: null, phoneNumberId: null, tps: '', webhookType: '0' })}
                className="text-gray-400 hover:text-gray-600 text-xl"
              >
                &times;
              </button>
            </div>
            <div className="px-5 py-4 flex flex-col gap-3">
              <div>
                <label className="text-sm text-gray-600 mb-1 block">TPS</label>
                <input
                  type="number"
                  min="0"
                  value={tpsModal.tps}
                  onChange={e => setTpsModal(m => ({ ...m, tps: e.target.value }))}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
                  placeholder="e.g. 60"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600 mb-1 block">User Webhook Type</label>
                <select
                  value={tpsModal.webhookType}
                  onChange={e => setTpsModal(m => ({ ...m, webhookType: e.target.value }))}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
                >
                  <option value="0" disabled>Select Webhook Type</option>
                  <option value="1">Default</option>
                  <option value="2">Moengage</option>
                  <option value="3">CleverTap</option>
                  <option value="4">WebEngage</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t">
              <button
                onClick={() => setTpsModal({ open: false, accountId: null, phoneNumberId: null, tps: '', webhookType: '0' })}
                className="px-4 py-1.5 border border-gray-300 text-gray-600 text-sm rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={saveTPS}
                className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Main Whatsapp Component ──────────────────────────────────────────────────
const INNER_TABS = [
  { id: 'accountSettings',   label: 'Account Settings',   icon: <CiSettings /> },
  { id: 'messageManagement', label: 'Message Management', icon: <MdMailOutline /> },
  { id: 'channelList',       label: 'Channel List',       icon: <MdPhoneAndroid /> },
]

// FIX 1: No duplicate prop — accept both client_id (selected user) and user_id (logged-in user)
// FIX 2: Resolve the correct ID — client_id (the selected user) takes priority
function Whatsapp({ user_id = '', client_id = '' }) {
  const resolvedId = client_id || user_id
  const [activeTab, setActiveTab] = useState('messageManagement')

  return (
    <div className="w-full flex mt-2 flex-col md:flex-row">
      {/* Sidebar */}
      <div className="w-full md:w-[200px] flex-shrink-0 py-2 border-b md:border-b-0 md:border-r border-[#c9c3c3]">
        <ul className="flex flex-row md:flex-col gap-0">
          {INNER_TABS.map(tab => (
            <li key={tab.id}>
              <button
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-2 text-left text-[13px] font-medium px-3 py-2 transition-all ${
                  activeTab === tab.id
                    ? 'text-[#448c74] border-b-2 md:border-b-0 md:border-l-2 border-[#448c74] bg-[#f0faf6]'
                    : 'text-[#576071] hover:bg-gray-50 border-b-2 md:border-b-0 md:border-l-2 border-transparent'
                }`}
              >
                <span className="text-base">{tab.icon}</span>
                <span className="hidden md:inline">{tab.label}</span>
                <span className="md:hidden text-xs">{tab.label.split(' ')[0]}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Tab Content — FIX 3: all tabs receive resolvedId */}
      <div className="flex-1 pl-0 md:pl-5 min-w-0">
        {activeTab === 'accountSettings'   && <AccountSettingsTab   user_id={resolvedId} />}
        {activeTab === 'messageManagement' && <MessageManagementTab user_id={resolvedId} />}
        {activeTab === 'channelList'       && <ChannelListTab        user_id={resolvedId} />}
      </div>
    </div>
  )
}

export default Whatsapp